import React, { Component } from 'react';
import { inject, observer } from 'mobx-react';
import autobind from 'autobind-decorator';
import { addStore } from 'client/shared/react-util';
import { FieldFactory } from 'client/shared/components';

@inject('formStore', 'userStore', 'artistStore', 'genreStore')
@observer
class BandForm extends Component {
	constructor(props) {
		super(props);

		const { user, artist } = this.props;

		// dirtyKeys is a Set of FieldFactory keys like 'artist.name' /
		// 'user.firstName'. We only PATCH the fields the user actually changed,
		// scoped to the right object — avoids transmitting empty strings for
		// untouched enum fields like gender, which trips Mongoose validation
		// and was crashing the API container.
		this.state = {
			saveDisabled: true,
			saveVisible: false,
			artistName: artist.name,
			userEmail: user.email,
			dirtyKeys: new Set(),
		}
	}

	componentDidMount () {
		const {genreStore} = this.props;
		genreStore.fetchIfEmpty();
	}

	render() {
        const { user, artist, formStore, genreStore } = this.props;

		const FieldWithStore = addStore(FieldFactory, { formStore });

		if(genreStore.isLoading) {

			return <p>Band Form Loading</p>;
		}

		return (
			<section id="section-account-info" className="acms-section acms-section--account-info">
				<h3 className="section-title artist heading acms-section-heading">Account Info</h3>

				<div className="grid-x grid-margin-x margin-top-1 acms-band-form-grid">
					<div className="cell small-12 medium-6">
						<div style={{flexDirection: 'column'}}>
							<div className="margin-bottom-2">
								{/* <input className="account-profile-field" defaultValue={artist.name} placeholder="Band/Artist Name" onChange={this.onInformationChange}></input>
								*/}
								<FieldWithStore
									item={ artist }
									fieldKey="artist.name"
									defaultValue={artist.name}
									onChange={(evt) => this.onInformationChange(evt)}
								/>
							</div>
							<div className="margin-bottom-2">
								{/* <input className="account-profile-field" defaultValue={user._email} placeholder="Email" onChange={this.onInformationChange}></input> */}
								<FieldWithStore
									item={ user }
									fieldKey="user.email"
									defaultValue={user.email}
									onChange={(evt) => this.onInformationChange(evt)}
								/>
							</div>
							<div className="margin-bottom-2">
								{/* <input className="account-profile-field" placeholder="Stripe Email" onChange={this.onInformationChange}></input>					 */}
								<FieldWithStore
									item={ artist }
									fieldKey="artist.paymentServiceID"
									defaultValue={artist.paymentServiceID}
									onChange={(evt) => this.onInformationChange(evt)}
								/>
							</div>
							<div className="margin-bottom-2" style={{position:'relative'}}>
								{/* <input className="account-profile-field" defaultValue={user.location} placeholder="Location" onChange={this.onInformationChange}></input>					 */}
								<FieldWithStore
									item={ user }
									fieldKey="user.location"
									defaultValue={user.location}
									onChange={(evt) => this.onInformationChange(evt)}
									style={{zIndex:3000}}
								/>
								<i className="fas fa-caret-down" style={{
										position:"absolute",
										top: "55%",
										right: "40%"
								}}/>
							</div>
							{
								<div className="margin-bottom-2">
									<button
										type="button"
										className="button neo acms-btn-save"
										onClick={ this.onSaveClicked }
										disabled={ this.state.saveDisabled }
									>
										Save
									</button>
								</div>
							}
						</div>
					</div>
					<div className="cell small-12 medium-6">
						<div style={{flexDirection: 'column'}}>
							<div className="margin-bottom-2">
								{/* <input className="account-profile-field" defaultValue={user._firstName} placeholder="First Name" onChange={this.onInformationChange}></input> */}
								<FieldWithStore
									item={ user }
									fieldKey="user.firstName"
									defaultValue={user.firstName}
									onChange={(evt) => this.onInformationChange(evt)}
								/>
							</div>
							<div className="margin-bottom-2">
								{/* <input className="account-profile-field" defaultValue={user._lastName} placeholder="Last Name" onChange={this.onInformationChange}></input> */}
								<FieldWithStore
									item={ user }
									fieldKey="user.lastName"
									defaultValue={user.lastName}
									onChange={(evt) => this.onInformationChange(evt)}
								/>
							</div>
							<div className="margin-bottom-2" style={{position:'relative'}}>
								<FieldWithStore
									className="hide-input"
									item={ artist }
									fieldKey="artist.genre"
									optionsStore={genreStore}
									defaultValue={artist.genre}
									defaultWidth={true}
									onChange={(e) => this.onSelectChange(e)}
								/>
							</div>
							<div className="margin-bottom-2 acms-account-security">
								<button
								 type="button"
								 className="acms-link-button"
								 onClick={ this.onResetPasswordClick }
								>
									Reset password…
								</button>
							</div>
						</div>
					</div>
				</div>
			</section>
		);
	}

	@autobind
	onResetPasswordClick () {
		const path = '/reset-password/';

		if (typeof window !== 'undefined') {
			window.location.assign(`${window.location.origin}${path}`);
		}
	}

	@autobind
	onSelectGenre (evt) {
		console.log(evt.value);
	}

	@autobind
	onSelectChange (evt) {
		this.markDirty('artist.genre');
	}

	@autobind
	onInformationChange (fieldKey) {
		// FieldFactory.handleChange already wrote the new value into the model
		// via the observable setter and then calls us with (fieldKey, value).
		// Just mark dirty — no need to re-validate the DOM. The previous DOM
		// lookup via document.getElementsByName broke for <ReactSelect> fields
		// (location, gender) because react-select renders custom DOM, not a
		// named input — so changing only location never enabled Save and the
		// value never made it into the dirty set.
		this.markDirty(fieldKey);
	}

	markDirty (fieldKey) {
		const dirtyKeys = new Set(this.state.dirtyKeys);
		dirtyKeys.add(fieldKey);
		this.setState({ saveDisabled: false, dirtyKeys });
	}

	/**
	 * PATCH only the fields the user actually edited, on the object they
	 * belong to. Field keys are namespaced ('artist.name' vs 'user.email');
	 * we split, build two payloads, and skip whichever side has no changes.
	 */
	@autobind
	async onSaveClicked () {
		const { artist, user, artistStore, userStore } = this.props;
		const { dirtyKeys } = this.state;

		const artistFields = {};
		const userFields = {};
		for (const key of dirtyKeys) {
			const [scope, field] = key.split('.');
			if (scope === 'artist' && field) {
				// `genre` on the client is a virtual; the persisted field is genreID.
				if (field === 'genre') artistFields.genreID = artist.genreID;
				else                   artistFields[field]  = artist[field];
			}
			if (scope === 'user' && field) {
				userFields[field] = user[field];
			}
		}

		const requests = [];
		if (Object.keys(artistFields).length) {
			requests.push(artistStore.patchArtist(artist.id, artistFields));
		}
		if (Object.keys(userFields).length) {
			requests.push(userStore.patchUser(user.id, userFields));
		}
		await Promise.all(requests);

		this.setState({ dirtyKeys: new Set(), saveDisabled: true });
	}

}

export default BandForm;
